import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, UserPlus, Shield, Trash2, Settings } from 'lucide-react';

import { API_URL } from '../config';
const API = API_URL;

const AdminManagement = ({ user, onLogout }) => {
  const [admins, setAdmins] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: ''
  });
  const [permissions, setPermissions] = useState({
    create_post: true,
    edit_post: true,
    delete_post: true,
    view_stats: true,
    manage_users: false,
    manage_admins: false
  });

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/super-admin/list-admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmins(response.data.admins);
    } catch (error) {
      toast.error('Failed to load admins');
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/super-admin/create-admin`,
        { ...formData, permissions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Admin created successfully');
      setShowCreateModal(false);
      setFormData({ email: '', name: '' });
      setPermissions({
        create_post: true,
        edit_post: true,
        delete_post: true,
        view_stats: true,
        manage_users: false,
        manage_admins: false
      });
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    }
  };

  const handleUpdatePermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/super-admin/update-admin-permissions`,
        {
          email: selectedAdmin.email,
          permissions: permissions
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Permissions updated successfully');
      setShowPermissionsModal(false);
      setSelectedAdmin(null);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    }
  };

  const handleRemoveAdmin = async (email, name) => {
    if (!window.confirm(`Are you sure you want to remove ${name} as admin?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/super-admin/remove-admin/${email}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Admin removed successfully');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove admin');
    }
  };

  const openPermissionsModal = (admin) => {
    setSelectedAdmin(admin);
    setPermissions(admin.permissions || {
      create_post: true,
      edit_post: true,
      delete_post: true,
      view_stats: true,
      manage_users: false,
      manage_admins: false
    });
    setShowPermissionsModal(true);
  };

  return (
    <div className="min-h-screen" style={{ background: '#050505', color: '#EDEDED' }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ background: '#121212', borderBottom: '1px solid #2A2A2A' }}>
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => window.location.href = '/admin'}
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-white"
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                <Shield className="w-6 h-6" style={{ color: '#EE1C4E' }} />
                Admin Management
              </h1>
              <p className="text-sm text-gray-400">Manage administrators and permissions</p>
            </div>
          </div>
          
          <Button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-2 text-white font-bold rounded-full"
            style={{ background: '#EE1C4E' }}
            data-testid="create-admin-button"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Create Admin
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="p-4 rounded-xl" style={{ background: '#121212', border: '1px solid #2A2A2A' }}>
            <div className="text-3xl font-bold" style={{ color: '#EE1C4E' }}>{admins.length}</div>
            <div className="text-sm text-gray-400">Total Admins</div>
          </div>
          <div className="p-4 rounded-xl" style={{ background: '#121212', border: '1px solid #2A2A2A' }}>
            <div className="text-3xl font-bold">{admins.filter(a => a.role === 'super_admin').length}</div>
            <div className="text-sm text-gray-400">Super Admins</div>
          </div>
          <div className="p-4 rounded-xl" style={{ background: '#121212', border: '1px solid #2A2A2A' }}>
            <div className="text-3xl font-bold">{admins.filter(a => a.role === 'admin').length}</div>
            <div className="text-sm text-gray-400">Regular Admins</div>
          </div>
        </div>

        {/* Admins List */}
        <h2 className="text-xl font-bold mb-4">All Administrators</h2>
        <div className="space-y-4">
          {admins.map((admin) => (
            <div
              key={admin.email}
              className="p-4 rounded-xl"
              style={{ background: '#121212', border: '1px solid #2A2A2A' }}
              data-testid={`admin-item-${admin.email}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-bold">{admin.name}</h3>
                    <span
                      className="px-3 py-1 rounded-full text-xs font-bold"
                      style={{
                        background: admin.role === 'super_admin' ? '#EE1C4E' : '#2A2A2A',
                        color: 'white'
                      }}
                    >
                      {admin.role === 'super_admin' ? 'SUPER ADMIN' : 'ADMIN'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-400 mb-3">{admin.email}</p>
                  
                  {/* Permissions */}
                  {admin.permissions && (
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(admin.permissions).map(([key, value]) => (
                        value && (
                          <span
                            key={key}
                            className="px-2 py-1 rounded text-xs"
                            style={{ background: '#1A1A1A', color: '#A1A1A1' }}
                          >
                            {key.replace('_', ' ')}
                          </span>
                        )
                      ))}
                    </div>
                  )}
                </div>
                
                {admin.role !== 'super_admin' && (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => openPermissionsModal(admin)}
                      variant="ghost"
                      size="sm"
                      className="text-gray-400 hover:text-white"
                      data-testid={`edit-permissions-${admin.email}`}
                    >
                      <Settings className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleRemoveAdmin(admin.email, admin.name)}
                      variant="ghost"
                      size="sm"
                      className="text-gray-400 hover:text-red-500"
                      data-testid={`remove-admin-${admin.email}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Create Admin Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent
          className="max-w-2xl"
          style={{ background: '#121212', border: '1px solid #2A2A2A', color: 'white' }}
          data-testid="create-admin-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ color: '#EE1C4E' }}>
              Create New Admin
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateAdmin} className="space-y-4">
            <div>
              <Label className="text-gray-300 mb-2 block">User Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="user@example.com"
                required
                className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                data-testid="admin-email-input"
              />
              <p className="text-xs text-gray-500 mt-1">User must have an existing account</p>
            </div>

            <div>
              <Label className="text-gray-300 mb-2 block">Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Admin name"
                required
                className="bg-[#1A1A1A] border-[#2A2A2A] text-white"
                data-testid="admin-name-input"
              />
            </div>

            <div>
              <Label className="text-gray-300 mb-3 block">Permissions</Label>
              <div className="space-y-3">
                {Object.keys(permissions).map((key) => (
                  <div key={key} className="flex items-center gap-2">
                    <Checkbox
                      checked={permissions[key]}
                      onCheckedChange={(checked) =>
                        setPermissions({ ...permissions, [key]: checked })
                      }
                      data-testid={`permission-${key}`}
                    />
                    <label className="text-sm text-gray-300 capitalize">
                      {key.replace('_', ' ')}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                onClick={() => setShowCreateModal(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 text-white font-bold"
                style={{ background: '#EE1C4E' }}
                data-testid="submit-create-admin"
              >
                Create Admin
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Permissions Modal */}
      <Dialog open={showPermissionsModal} onOpenChange={setShowPermissionsModal}>
        <DialogContent
          className="max-w-md"
          style={{ background: '#121212', border: '1px solid #2A2A2A', color: 'white' }}
          data-testid="edit-permissions-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ color: '#EE1C4E' }}>
              Edit Permissions
            </DialogTitle>
            {selectedAdmin && (
              <p className="text-sm text-gray-400">{selectedAdmin.name}</p>
            )}
          </DialogHeader>
          
          <div className="space-y-3">
            {Object.keys(permissions).map((key) => (
              <div key={key} className="flex items-center gap-2">
                <Checkbox
                  checked={permissions[key]}
                  onCheckedChange={(checked) =>
                    setPermissions({ ...permissions, [key]: checked })
                  }
                  data-testid={`edit-permission-${key}`}
                />
                <label className="text-sm text-gray-300 capitalize">
                  {key.replace('_', ' ')}
                </label>
              </div>
            ))}
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              onClick={() => setShowPermissionsModal(false)}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdatePermissions}
              className="flex-1 text-white font-bold"
              style={{ background: '#EE1C4E' }}
              data-testid="submit-update-permissions"
            >
              Update Permissions
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminManagement;
